import { Box, Chip, Divider, Skeleton, Typography } from "@mui/material";
import { IconCaretDown, IconCaretUp, IconMinus } from "@tabler/icons-react";
import { Moment } from "moment";
import MoedaText from "../../util/ui/inputs/moedaText";
import PercentualText from "../../util/ui/inputs/percentualText";
import { useSaldoBruto } from "../analises/useSaldoBruto";
import { radius } from "../../../theme";

type Props = {
  mes: Moment;
};

/**
 * Identidade do mês + quanto tem, num bloco só.
 *
 * Antes eram duas coisas soltas na primeira row: um título com dois botões e um
 * card de Saldo bruto do outro lado da tela. Só que as duas respondem a mesma
 * pergunta — que mês estou olhando e quanto tem nele — então viraram uma ficha.
 * As ações da tela saíram daqui e foram para o topo do painel de situação, que
 * é onde o olho já está.
 */
export default function FichaDoMes({ mes }: Props) {
  const { saldoBruto, loading, variacao } = useSaldoBruto(mes);

  // Variação mapeia para uma das cinco cores semânticas de chip do sistema.
  const statusVariacao =
    variacao > 0 ? "positive" : variacao < 0 ? "destructive" : "neutral";
  const IconeVariacao =
    variacao > 0 ? IconCaretUp : variacao < 0 ? IconCaretDown : IconMinus;

  const carregando = loading || !saldoBruto;

  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        p: 2.5,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${radius.lg}px`,
      })}
    >
      <Box>
        <Typography variant="eyebrow" color="text.secondary">
          Competência
        </Typography>
        <Typography
          variant="h1"
          color="text.primary"
          sx={{ textTransform: "uppercase", mt: 0.25 }}
        >
          {mes.format("MMMM/YY")}
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="labelMono" component="p" color="text.secondary">
          Saldo bruto
        </Typography>
        <Typography
          variant="dataMono"
          component="p"
          color="text.primary"
          sx={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", mt: 0.5 }}
        >
          {carregando ? (
            <Skeleton variant="text" width={180} animation={false} />
          ) : (
            <MoedaText value={saldoBruto.saldoBruto} />
          )}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="labelMono" color="text.secondary">
          Aplicado
        </Typography>
        <Typography variant="dataMono" color="text.secondary">
          {carregando ? (
            <Skeleton variant="text" width={90} animation={false} />
          ) : (
            <MoedaText value={saldoBruto.valorAplicado} />
          )}
        </Typography>
        {!carregando && (
          <Chip
            size="small"
            sx={(theme) => ({
              backgroundColor: theme.palette.statusChip[statusVariacao].bg,
              color: theme.palette.statusChip[statusVariacao].fg,
              "& .MuiChip-label": {
                display: "flex",
                alignItems: "center",
                gap: "2px",
              },
            })}
            label={
              <>
                <IconeVariacao size={12} strokeWidth={3} />
                <PercentualText value={variacao} decimalScale={2} />
              </>
            }
          />
        )}
      </Box>

      <Typography variant="labelMono" sx={{ color: "text.disabled" }}>
        {carregando ? (
          <Skeleton variant="text" width={80} animation={false} />
        ) : (
          `em ${saldoBruto.data.format("DD/MM/YY")}`
        )}
      </Typography>
    </Box>
  );
}
